/**
 * Self-hosted Web Push (VAPID) client helper.
 *
 * Adds Badging API support (navigator.setAppBadge) to the Find-2-Day PWA.
 * Works alongside the existing OneSignal channel — this one targets the main
 * service worker at /service-worker.js (NOT the OneSignal SW under /onesignal/).
 *
 * Platform support (accurate as of Jul 2026):
 *   - iOS Safari 16.4+ (installed PWA only): NUMERIC badge via setAppBadge
 *   - Desktop Chrome / Edge (installed PWA): NUMERIC badge via setAppBadge
 *   - Android Chrome (installed PWA): DOT ONLY — Android does not implement
 *     the Badging API; the launcher shows a dot while an unread system
 *     notification is in the tray (never a number, regardless of launcher).
 *   - In-browser Safari on iOS: no badge (Apple limitation)
 */

const API = process.env.REACT_APP_BACKEND_URL;

/** URL-safe base64 → Uint8Array (required for applicationServerKey). */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isWebPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function isBadgingSupported() {
  return typeof navigator !== 'undefined' && typeof navigator.setAppBadge === 'function';
}

async function fetchPublicKey() {
  const res = await fetch(`${API}/api/web-push/vapid-public-key`);
  if (!res.ok) throw new Error('Failed to fetch VAPID public key');
  const data = await res.json();
  if (!data.publicKey) throw new Error('VAPID public key missing');
  return data.publicKey;
}

/**
 * Run the full opt-in flow:
 *   1. Request notification permission (must be triggered by a user gesture).
 *   2. Get the existing main service-worker registration.
 *   3. Reuse the existing subscription or create a new one with VAPID.
 *   4. POST it to /api/web-push/subscribe alongside the device subscriptionId.
 *
 * Returns the saved PushSubscription on success. Throws with a descriptive
 * error otherwise so callers can surface a toast.
 */
export async function enableWebPushBadge(subscriptionId, authToken) {
  if (!isWebPushSupported()) {
    throw new Error('This browser does not support Web Push.');
  }
  if (!subscriptionId) {
    throw new Error('Missing device subscriptionId.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission was not granted.');
  }

  // Use the registration whose scope covers '/' — that's our main SW. We
  // explicitly request it to avoid accidentally picking up the OneSignal SW
  // which is scoped under /onesignal/.
  const registration =
    (await navigator.serviceWorker.getRegistration('/')) ||
    (await navigator.serviceWorker.ready);
  if (!registration) {
    throw new Error('Service worker not ready.');
  }

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    const publicKey = await fetchPublicKey();
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    // Persist key + subId so the SW can resubscribe on pushsubscriptionchange.
    try {
      const cache = await caches.open('find2day-vapid');
      await cache.put('public-key', new Response(publicKey));
      await cache.put('sub-id', new Response(subscriptionId));
    } catch (_) { /* non-fatal */ }
  }

  const headers = { 'Content-Type': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

  const res = await fetch(`${API}/api/web-push/subscribe`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      subscriptionId,
      subscription: subscription.toJSON(),
    }),
  });
  if (!res.ok) {
    throw new Error('Failed to save subscription on the server.');
  }
  return subscription;
}

export async function disableWebPushBadge(subscriptionId) {
  if (!isWebPushSupported()) return;
  const registration = await navigator.serviceWorker.getRegistration('/');
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  try { await subscription.unsubscribe(); } catch (_) { /* noop */ }
  try {
    await fetch(`${API}/api/web-push/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ subscriptionId, endpoint }),
    });
  } catch (_) { /* noop */ }
  try {
    if (isBadgingSupported() && typeof navigator.clearAppBadge === 'function') {
      await navigator.clearAppBadge();
    }
  } catch (_) { /* noop */ }
}

/** Apply a badge count from window context. Safe across all platforms. */
export async function applyAppBadge(count) {
  if (!isBadgingSupported()) return false;
  try {
    if (typeof count === 'number' && count > 0) {
      await navigator.setAppBadge(count);
    } else if (typeof navigator.clearAppBadge === 'function') {
      await navigator.clearAppBadge();
    }
    return true;
  } catch (_) {
    return false;
  }
}

/** Fire a server-driven test push to the current caller. */
export async function sendTestWebPush(subscriptionId, authToken) {
  const headers = {};
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const url = `${API}/api/web-push/test?subscriptionId=${encodeURIComponent(subscriptionId || '')}`;
  const res = await fetch(url, { method: 'POST', headers });
  if (!res.ok) throw new Error('Test push request failed.');
  return res.json();
}
